import React, { useState, KeyboardEvent, useRef, useEffect } from "react";
import { Button, Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import "./ChatInput.scss";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, isLoading }) => {
  const [inputValue, setInputValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && !isLoading) {
      onSendMessage(trimmedValue);
      setInputValue("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.currentTarget.value);
  };

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      const maxHeight = 120; // Max height for ~4 lines
      textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [inputValue]);

  return (
    <div className="chat-input">
      <div className="chat-input-container">
        <div className="chat-input-field">
          <textarea
            ref={textareaRef}
            id="chatMessage"
            name="chatMessage"
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
            disabled={isLoading}
            aria-label="Chat message input"
            maxLength={1000}
            rows={1}
            className="chat-input-textarea"
          />
        </div>
        <Button
          variant="primary"
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading}
          aria-label="Send message"
          className="chat-input-send-button"
          icon={<Iconography icon="arrowRight" />}
        />
      </div>
    </div>
  );
};

export default ChatInput;

